import React, { createContext, useContext, useMemo } from 'react'

import { DEFAULT_LOCALE } from '../constants'
import { localizeHref } from '../helpers/localizeHref'
import { createTranslate, Translate } from '../i18n'

export type Locale = 'en-CA' | 'fr-CA'

export const FRENCH_LOCALE: Locale = 'fr-CA'

// Site locale -> value for `<html lang>`.
export const HTML_LANG: Record<Locale, string> = {
  'en-CA': 'en-CA',
  'fr-CA': 'fr-CA',
}

// Site locale -> `hreflang` value.
export const HREFLANG: Record<Locale, string> = {
  'en-CA': 'en-CA',
  'fr-CA': 'fr-CA',
}

export interface ILocaleContext {
  locale: Locale
  lang: string
  isFrench: boolean
  // Same page in every locale it's built in, keyed by site locale. Empty when only one exists.
  alternates: Partial<Record<Locale, string>>
  // Rewrites an internal link to the current locale's version of the page when one is built.
  localizeHref: (href: string) => string
  // UI text that lives in code, in the page's locale. See src/i18n.
  t: Translate
}

const keepHref = (href: string) => href

const LocaleContext = createContext<ILocaleContext>({
  locale: DEFAULT_LOCALE,
  lang: HTML_LANG[DEFAULT_LOCALE],
  isFrench: false,
  alternates: {},
  localizeHref: keepHref,
  t: createTranslate(DEFAULT_LOCALE),
})

export const useLocale = () => useContext(LocaleContext)

interface IProps {
  // Code pages (`/blog/archive`, `/404`) don't get a locale in their page context and stay English.
  locale?: Locale
  alternates?: ILocaleContext['alternates']
}

// Don't add static queries here: in Gatsby 3 a useStaticQuery in a component rendered only from wrapPageElement
// fails with "could not be fetched".
export const LocaleContextProvider: React.FC<IProps> = ({ locale = DEFAULT_LOCALE, alternates, children }) => {
  const value = useMemo<ILocaleContext>(
    () => ({
      locale,
      lang: HTML_LANG[locale],
      isFrench: locale === FRENCH_LOCALE,
      alternates: alternates ?? {},
      localizeHref: keepHref,
      t: createTranslate(locale),
    }),
    [locale, alternates],
  )
  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
}

// Adds French link rewriting under the page's LocaleContextProvider. Rendered by FrenchLinks, which owns the static
// query for the built French paths: this file must stay free of static queries (see above).
export const LocalizedLinksProvider: React.FC<{ frenchPaths: string[] }> = ({ frenchPaths, children }) => {
  const parent = useLocale()
  const value = useMemo<ILocaleContext>(() => {
    if (!parent.isFrench) return parent
    const frenchPathSet = new Set(frenchPaths)
    return { ...parent, localizeHref: href => localizeHref(href, frenchPathSet) }
  }, [parent, frenchPaths])
  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
}

// Static queries can't take variables, so global components query every locale and pick one here.
// Falls back to the English node when the French one isn't there (fr-CA not sourced, or not translated).
export const usePickLocalized = <T,>(byLocale: { 'en-CA': T } & Partial<Record<Locale, T | null>>): T => {
  const { locale } = useLocale()
  return byLocale[locale] ?? byLocale[DEFAULT_LOCALE]
}
